import { useState, useEffect } from 'react';
import { FiUser, FiShield, FiMail, FiPhone, FiBriefcase, FiLogOut, FiCreditCard, FiCheck, FiBell } from 'react-icons/fi';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import DevicesCard from './DevicesCard';

// Plan code → display label mapping. Kept in sync with plans.config.js on the backend.
// Single tier since 2026-07-31: the only difference between plans is how often
// you are billed. The retired athena-* codes are still mapped so a legacy
// subscriber sees their real plan and price rather than a raw plan code.
const PLAN_LABELS = {
  'ares-monthly': { name: 'Monthly', priceLabel: '$9.99/mo' },
  'ares-annual': { name: 'Annual', priceLabel: '$119.88/yr' },
  'athena-monthly': { name: 'Monthly (legacy)', priceLabel: '$5.99/mo' },
  'athena-annual': { name: 'Annual (legacy)', priceLabel: '$71.88/yr' },
};

const AccountManagement = () => {
  const { user, subscription, loading } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Phase 3 profile + notification preferences
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    display_name: '', phone: '', company: '', timezone: '', marketing_opt_in: false,
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, nRes] = await Promise.all([
          fetch(`${API_BASE_URL}/bff/profile`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/bff/profile/notifications-prefs`, { credentials: 'include' }),
        ]);
        if (pRes.ok) {
          const { profile: p } = await pRes.json();
          if (!cancelled && p) {
            setProfile(p);
            setProfileForm({
              display_name: p.display_name || '',
              phone: p.phone || '',
              company: p.company || '',
              timezone: p.timezone || '',
              marketing_opt_in: !!p.marketing_opt_in,
            });
          }
        }
        if (nRes.ok) {
          const { prefs: pr } = await nRes.json();
          if (!cancelled) setPrefs(pr);
        }
      } catch (e) {
        console.error('[AccountManagement] profile load error:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (k, v) => setProfileForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/bff/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.errors?.[0]?.msg || data.error || 'Failed to save');
      }
      setProfile(data.profile);
      setProfileMsg({ ok: true, text: 'Saved' });
    } catch (err) {
      setProfileMsg({ ok: false, text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  const togglePref = async (key) => {
    if (!prefs) return;
    const desired = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: desired }));
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/bff/profile/notifications-prefs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: desired }),
      });
      const data = await res.json();
      if (res.ok && data.prefs) setPrefs(data.prefs);
      else throw new Error('save failed');
    } catch (e) {
      setPrefs((p) => ({ ...p, [key]: !desired })); // revert
    }
  };

  // Common IANA zones for the timezone picker (kept short; backend accepts any valid IANA zone).
  const TIMEZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix', 'America/Toronto',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Kolkata',
    'Australia/Sydney', 'UTC',
  ];

  const NOTIF_GROUPS = [
    { label: 'CyberFraud alerts', email: 'email_psa_alerts', inapp: 'inapp_psa_alerts' },
    { label: 'Domain rating alerts', email: 'email_domain_alerts', inapp: 'inapp_domain_alerts' },
    { label: 'FBI IC3 alerts', email: 'email_ic3_alerts', inapp: 'inapp_ic3_alerts' },
  ];

  const handleLogout = () => {
    const clearB2C = window.confirm(
      'Do you want to sign out from all devices?\n\n' +
      'Yes = Sign out everywhere (recommended for shared computers)\n' +
      'No = Sign out from this device only'
    );
    window.location.href = `${API_BASE_URL}/auth/logout${clearB2C ? '?clearB2C=true' : ''}`;
  };

  // Open Stripe Customer Portal for payment / invoice / plan management
  const openBillingPortal = async () => {
    setPortalLoading(true);
    setActionError(null);
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/payment/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to open billing portal');
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('[AccountManagement] Portal error:', err);
      setActionError(err.message);
      setPortalLoading(false);
    }
  };

  // The Athena → Ares upgrade handler was removed with the single tier: there is
  // no higher plan to move to. Switching between monthly and annual billing is
  // done in the Stripe Customer Portal, which the Manage Billing button opens.

  // Format current_period_end timestamp for display
  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-white flex items-center justify-center">
        <div className="text-gray-500">Loading account information...</div>
      </div>
    );
  }

  const nextBilling = formatDate(subscription?.currentPeriodEnd);

  // The Stripe billing portal needs a Stripe customer. A plan can be active
  // without one — dev/mock activations never touch Stripe — so explain that
  // rather than telling an active subscriber to "subscribe first".
  const billingUnavailableReason = subscription?.stripeCustomerId
    ? null
    : subscription?.source === 'mock'
      ? 'This plan was activated for development and isn’t linked to Stripe, so there is no billing portal for it.'
      : subscription?.status === 'active'
        ? 'This plan isn’t linked to a Stripe customer, so the billing portal can’t be opened. Contact support to reconnect billing.'
        : 'Subscribe to manage payment methods, invoices and plan changes.';

  return (
    <HubPageLayout>
      <FramedPanel title="Account Management">
        <div className="px-6 pt-8 pb-6">
          <div className="flex flex-col md:flex-row gap-8">
            <img
              src="/images/my-noscam/account-management-hero.jpeg"
              alt=""
              className="md:w-1/2 h-[220px] md:h-[260px] object-cover"
            />
            <div className="md:w-1/2 md:pt-2">
              <p className="text-lg text-black border-b border-gray-400 pb-3">Account Summary</p>
              <p className="text-lg text-black border-b border-gray-400 py-3">
                Next Billing / {nextBilling || '—'}
              </p>
              <p className="text-lg text-black border-b border-gray-400 py-3">
                Plan / {PLAN_LABELS[subscription?.planCode]?.name || subscription?.planCode || 'None'}
              </p>
            </div>
          </div>
        </div>
      </FramedPanel>

          {/* Content — pt- keeps the first card off the hero panel, which it
              previously butted straight against. */}
          <div className="pl-12 pr-8 pb-12 pt-8">

            {/* Account Information Card */}
            <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FiUser className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-black">Account Information</h2>
                  <p className="text-gray-500 text-sm">Your profile details</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Display Name</label>
                  <input
                    type="text"
                    value={profileForm.display_name}
                    onChange={(e) => setField('display_name', e.target.value)}
                    placeholder={user?.name || 'Your name'}
                    className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Email Address</label>
                  <div className="mt-1 w-full px-4 py-2 bg-gray-50 rounded-xl text-black font-medium flex items-center gap-2">
                    <FiMail className="w-4 h-4 text-gray-400" />
                    {profile?.email || user?.email || 'Not available'}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Phone Number</label>
                  <div className="mt-1 flex items-center gap-2">
                    <FiPhone className="w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="(555) 555-5555"
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Company</label>
                  <div className="mt-1 flex items-center gap-2">
                    <FiBriefcase className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={profileForm.company}
                      onChange={(e) => setField('company', e.target.value)}
                      placeholder="Company (optional)"
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Timezone</label>
                  <select
                    value={profileForm.timezone}
                    onChange={(e) => setField('timezone', e.target.value)}
                    className="mt-1 w-full px-4 py-2 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">Select timezone…</option>
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-3 md:mt-7">
                  <input
                    id="marketing_opt_in"
                    type="checkbox"
                    checked={profileForm.marketing_opt_in}
                    onChange={(e) => setField('marketing_opt_in', e.target.checked)}
                    className="w-5 h-5 rounded text-blue-600"
                  />
                  <label htmlFor="marketing_opt_in" className="text-sm text-gray-700">
                    Send me product news &amp; marketing emails
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={saveProfile}
                  disabled={profileSaving}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {profileSaving ? 'Saving…' : 'Save Profile'}
                </button>
                {profileMsg && (
                  <span className={`text-sm ${profileMsg.ok ? 'text-green-600' : 'text-red-600'}`}>
                    {profileMsg.text}
                  </span>
                )}
              </div>
            </div>

            {/* Notification Preferences Card */}
            <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <FiBell className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-black">Notifications</h2>
                  <p className="text-gray-500 text-sm">Choose how you want to be alerted</p>
                </div>
              </div>

              {!prefs ? (
                <p className="text-gray-400 text-sm">Loading preferences…</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200">
                  <div className="grid grid-cols-3 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <span>Alert type</span>
                    <span className="text-center">Email</span>
                    <span className="text-center">In-app</span>
                  </div>
                  {NOTIF_GROUPS.map((g) => (
                    <div key={g.label} className="grid grid-cols-3 items-center px-4 py-3 border-t border-gray-100">
                      <span className="text-sm text-gray-800">{g.label}</span>
                      <div className="text-center">
                        <input
                          type="checkbox"
                          checked={!!prefs[g.email]}
                          onChange={() => togglePref(g.email)}
                          className="w-5 h-5 rounded text-blue-600"
                        />
                      </div>
                      <div className="text-center">
                        <input
                          type="checkbox"
                          checked={!!prefs[g.inapp]}
                          onChange={() => togglePref(g.inapp)}
                          className="w-5 h-5 rounded text-blue-600"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Security Card */}
            <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <FiShield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-black">Security</h2>
                    <p className="text-gray-500 text-sm">Sign-in secured by Microsoft Entra ID</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-lg">🔐</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Password</p>
                  <p className="text-xs text-gray-500">Managed by Microsoft</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-lg">📱</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Two-Factor Auth</p>
                  <p className="text-xs text-gray-500">Managed by Microsoft</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-lg">🔔</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Login Alerts</p>
                  <p className="text-xs text-gray-500">New device &amp; location</p>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                Your sign-in, password, and two-factor settings are managed by Microsoft Entra ID — or by your sign-in provider (e.g. Google) if you signed in that way. To reset a password, choose &quot;Forgot password&quot; on the sign-in screen.
              </p>
            </div>

            {/* My Devices Card */}
            <DevicesCard />

            {/* Current Plan Card */}
            <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <FiCheck className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-black">Subscription</h2>
                    <p className="text-gray-500 text-sm">Your current plan</p>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  subscription?.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : subscription?.status === 'past_due'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {subscription?.status === 'active' ? 'Active'
                    : subscription?.status === 'past_due' ? 'Payment issue'
                    : subscription?.status === 'canceled' ? 'Canceled'
                    : subscription?.status || 'None'}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-black">
                      {PLAN_LABELS[subscription?.planCode]?.name || subscription?.planCode || 'No active plan'}
                    </h3>
                    <p className="text-gray-500">
                      {subscription?.status === 'active' || subscription?.status === 'past_due'
                        ? 'Full Desktop App + Web Dashboard access'
                        : 'Subscribe to access the Desktop App'}
                    </p>
                  </div>
                  {PLAN_LABELS[subscription?.planCode] && (
                    <div className="text-right">
                      <p className="text-3xl font-bold text-black">
                        {PLAN_LABELS[subscription.planCode].priceLabel}
                      </p>
                    </div>
                  )}
                </div>

                {/* Renewal / cancellation info */}
                {subscription?.currentPeriodEnd && (
                  <div className="pt-4 border-t border-gray-200 text-sm text-gray-600">
                    {subscription.cancelAtPeriodEnd ? (
                      <p className="text-orange-700">
                        <strong>Subscription ends on {formatDate(subscription.currentPeriodEnd)}.</strong>{' '}
                        You'll retain access until then.
                      </p>
                    ) : (
                      <p>
                        {subscription.status === 'active'
                          ? `Renews on ${formatDate(subscription.currentPeriodEnd)}`
                          : `Access until ${formatDate(subscription.currentPeriodEnd)}`}
                      </p>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Payment Method & Invoices — combined into a single "Manage Billing" card */}
            <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <FiCreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black">Billing</h3>
                    <p className="text-sm text-gray-500">Payment method, invoices, plan changes</p>
                  </div>
                </div>
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading || !subscription?.stripeCustomerId}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  title={billingUnavailableReason || ''}
                >
                  {portalLoading ? 'Opening...' : 'Manage Billing'}
                </button>
              </div>
              {actionError && (
                <p className="text-sm text-red-600 mb-2">{actionError}</p>
              )}
              <p className="text-xs text-gray-500">
                {billingUnavailableReason || (
                  <>
                    Manage your payment method, view invoices, change plans, or cancel — all handled
                    securely through Stripe&apos;s billing portal.
                  </>
                )}
              </p>
            </div>

            {/* Sign Out Section */}
            <div className="border-2 border-red-200 rounded-2xl p-6 bg-red-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-red-800">Sign Out</h2>
                  <p className="text-red-600 text-sm mt-1">End your current session</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
    </HubPageLayout>
  );
};

export default AccountManagement;
